import React, { useCallback, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { Grid, SelectField } from '../../../../../ui-toolkit';
import { SigningAppointmentDescriptionTooltip } from '../../../signingAppointments/SigningAppointmentDescriptionTooltip';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { BoldBodyTextTW } from '../../../../../tailwindComponents/bodyText/BodyTextTW';
import { ISigningAppointmentEditFormData } from '../../../../../types/signingAppointment/ISigningAppointmentEditFormData';
import { SigSyncAppointmentEditFormValidationSchema } from './SigSyncAppointmentEditFormValidationSchema';
import {
  getStatusDescriptions,
  getStatusOptions,
  ISigningAppointmentStatusResponse,
} from '../../../../../types/signingAppointment/ISigningAppointmentStatusResponse';
import {
  useListStatusesQuery,
  useUpdateMutation,
} from '../../../../../API/order/signingAppointment/SigningAppointmentAPI';
import { useSelectAppointmentFromPathParam } from '../../../../../utilities/order/scheduling/useSomeAppointmentFromParam';
import { eSigningAppointmentStatus } from '../../../../../types/signingAppointment/eSigningAppointmentStatus';
import { CancelOrSubmit } from '../CancelOrSubmit';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../../../types/shared/UUID';
import { checkForMutationSuccess } from '../../../../../API/checkForMutationSuccess';
export const sigSyncEnabledStatuses: eSigningAppointmentStatus[] = [eSigningAppointmentStatus.Cancelled];

export interface ISigSyncAppointmentEditFormProps {
  onClose: () => void;
}

export const SigSyncAppointmentEditForm: React.FC<ISigSyncAppointmentEditFormProps> = ({ onClose }) => {
  const { appointmentFromPath } = useSelectAppointmentFromPathParam();
  const { orderId, appointmentId } = useParams<{ orderId: UUID; appointmentId: UUID }>();
  const [updateSigningAppointment] = useUpdateMutation();

  const onSaveEditions = useCallback(
    async (data: ISigningAppointmentEditFormData) => {
      const updateResponse = await updateSigningAppointment({
        id: appointmentId,
        orderId,
        data,
      });
      if (checkForMutationSuccess(updateResponse, 'updating signing appointment')) {
        onClose();
      }
    },
    [updateSigningAppointment, orderId, appointmentId, onClose]
  );

  const { data: signingStatuses = [] } = useListStatusesQuery();
  const statuses: ISigningAppointmentStatusResponse[] = useMemo(
    () =>
      signingStatuses.filter((status) => {
        return sigSyncEnabledStatuses.includes(status.id as eSigningAppointmentStatus);
      }),
    [signingStatuses]
  );
  const statusDescriptions = useMemo(() => getStatusDescriptions(statuses), [statuses]);
  const statusOptions = useMemo(() => getStatusOptions(statuses), [statuses]);

  if (!appointmentFromPath) return null;
  return (
    <Formik
      initialValues={appointmentFromPath}
      validationSchema={SigSyncAppointmentEditFormValidationSchema}
      onSubmit={async (values: ISigningAppointmentEditFormData) => {
        await onSaveEditions({
          id: values.id,
          status: values.status,
        });
      }}
      enableReinitialize
    >
      <Form>
        <div className={'flex flex-row pb-5'}>
          <CalendarIcon className={'mr-2 h-5 w-5'} />
          <BoldBodyTextTW>Edit SigSync Appointment Details</BoldBodyTextTW>
          <CancelOrSubmit onClose={onClose} />
        </div>

        <div className={'space-y-4'}>
          <Grid columnCount={2} templateColumns={'.5fr .5fr'}>
            <SelectField
              data-test-id={`${appointmentFromPath.id}-scheduling-tab-status-select`}
              name={`status`}
              label="Status"
              placeholder={`Select a status`}
              options={statusOptions}
              formatOptionLabel={(option: any) => {
                return (
                  <SigningAppointmentDescriptionTooltip
                    type={'status'}
                    option={option}
                    optionDescriptions={statusDescriptions}
                  />
                );
              }}
            />
          </Grid>
        </div>
      </Form>
    </Formik>
  );
};
